/**
 * Shared lessons memory, as the web app sees it: the DTO and kind come from @roomate/core (#519),
 * the rest is presentation. The routes they describe are in docs/specs/agent-lessons.md §4.
 */
import type { LessonDTO as CoreLessonDTO, LessonKind } from "@roomate/core";

/**
 * Correction-lesson fields (docs/specs/correction-lessons.md §2.3). They are optional here so an
 * older hub, which sends none of them, still renders as evidence lessons.
 */
export type LessonDTO = CoreLessonDTO & {
  source?: "evidence" | "correction";
  ruleKey?: string | null;
  agentIds?: string[];
  wentLiveAt?: number | null;
};

export type { LessonKind };

export type CorrectionKind = "request_changes" | "reject" | "reversal";
export type CorrectionStatus = "pending" | "claimed" | "drafted" | "failed" | "skipped";

/** One decision that taught a rule (spec §2.5). */
export interface CorrectionSourceDTO {
  correctionId: string;
  decisionId: string;
  taskId: string;
  taskNumber: number;
  kind: CorrectionKind;
  answeredBy: string;
  answeredAt: number;
  recurrence: boolean;
}

/** `GET /api/agents/:id/correction-rules` entry (spec §2.5). */
export interface CorrectionRuleDTO {
  lesson: LessonDTO;
  sources: CorrectionSourceDTO[];
  runsSinceLive: number;
  recurrencesSinceLive: number;
}

/** `GET /api/agents/:id/corrections` entry: a correction of this agent, drafted or not. */
export interface CorrectionDTO {
  id: string;
  decisionId: string;
  taskId: string;
  taskNumber?: number;
  agentId: string;
  kind: CorrectionKind;
  status: CorrectionStatus;
  lessonId: string | null;
  ruleKey: string | null;
  recurrence: boolean;
  createdAt: number;
}

export const CORRECTION_KIND_LABEL: Record<CorrectionKind, string> = {
  request_changes: "changes requested",
  reject: "rejected",
  reversal: "reversed",
};

export const CORRECTION_STATUS_LABEL: Record<CorrectionStatus, string> = {
  pending: "waiting for a draft",
  claimed: "being drafted",
  drafted: "drafted",
  failed: "draft failed",
  skipped: "nothing to learn",
};

export type RuleStatus = "live" | "probation" | "retired";

export const RULE_STATUS_LABEL: Record<RuleStatus, string> = { live: "Live", probation: "On probation", retired: "Retired" };

export function ruleStatus(l: Pick<LessonDTO, "status" | "live">): RuleStatus {
  return l.status === "retired" ? "retired" : l.live ? "live" : "probation";
}

/** A rule has run counts only once it went live; a rule retired on probation never did. */
export const hasGoneLive = (l: Pick<LessonDTO, "wentLiveAt" | "live">): boolean => (l.wentLiveAt === undefined ? l.live : l.wentLiveAt !== null);

/** Swaps the returned lesson into cached rules, keeping each rule's sources and counts. */
export function patchRuleLesson(rules: CorrectionRuleDTO[] | undefined, lesson: LessonDTO): CorrectionRuleDTO[] | undefined {
  return rules?.map((r) => (r.lesson.id === lesson.id ? { ...r, lesson: { ...r.lesson, ...lesson } } : r));
}

export const isCorrectionLesson = (l: Pick<LessonDTO, "source">): boolean => l.source === "correction";

export type LessonSourceFilter = "all" | "evidence" | "correction";

export function filterLessonsBySource(lessons: LessonDTO[], filter: LessonSourceFilter): LessonDTO[] {
  if (filter === "all") return lessons;
  return lessons.filter((l) => (filter === "correction") === isCorrectionLesson(l));
}

/** Corrections that did not become a rule: everything not drafted into a lesson. */
export function unturnedCorrections(corrections: CorrectionDTO[]): CorrectionDTO[] {
  return corrections.filter((c) => !(c.status === "drafted" && c.lessonId));
}

/** "10 runs since live" */
export function runsSinceLiveText(runs: number): string {
  return `${runs} run${runs === 1 ? "" : "s"} since live`;
}

/** "2 repeats caught in review": only repeats a reviewer caught are counted. */
export function repeatsText(repeats: number): string {
  return `${repeats} repeat${repeats === 1 ? "" : "s"} caught in review`;
}

export const KIND_LABEL: Record<LessonKind, string> = {
  pitfall: "Pitfall",
  fix: "Fix",
  practice: "Practice",
};

/** The short handle the agent prompt uses (`[L<last 6>]`), so a merge link reads the same as the prompt. */
export function lessonRef(id: string): string {
  return `L${id.slice(-6)}`;
}

/** The DOM id of a lesson's row on the Lessons page; the "merged into" link and the run page point here. */
export function lessonAnchor(id: string): string {
  return `lesson-${id}`;
}

/** Areas as the prompt shows them, or "project-wide" when the lesson has none. */
export function lessonScope(areas: string[]): string {
  return areas.length ? areas.join(", ") : "project-wide";
}

/**
 * Splits a project's lessons into the two sections. Active ones show the most recently confirmed
 * first; retired ones the most recently retired first. Ties fall back to id so the order is stable.
 */
export function partitionLessons(lessons: LessonDTO[]): { active: LessonDTO[]; retired: LessonDTO[] } {
  const active = lessons.filter((l) => l.status === "active").sort((a, b) => b.lastConfirmedAt - a.lastConfirmedAt || a.id.localeCompare(b.id));
  const retired = lessons
    .filter((l) => l.status === "retired")
    .sort((a, b) => (b.retiredAt ?? 0) - (a.retiredAt ?? 0) || a.id.localeCompare(b.id));
  return { active, retired };
}
